import React, {
    useEffect, useState, useCallback,
} from 'react';
import _ from 'lodash';
import format from 'date-fns/format';
import PropTypes from 'prop-types';
import {
    TextField,
    FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import { useIntl } from 'react-intl';
import GridItem from '../../components/Grid/GridItem';
import GridContainer from '../../components/Grid/GridContainer';
import CustomTypography from '../../components/Typography/CustomTypography';
import CleanableInput from '../../components/Input/CleanableInput';
import Validation from '../../common/Validation';
import {
    AddBoxIcon,
    IndeterminateCheckBoxIcon, Trash,
} from '../../components/Icons/Icon';
import Button from '../../components/Button/CustomButton';
import registrationOrganizerStyle from '../../assets/jss/pages/registrationOrganizerStyle';
import CustomTable from '../../components/Table/Table';
import TimeRanger from '../../components/DatePicker/TimeRanger';
import SingleDatePickerSearch from '../../components/DatePicker/SingleDatePickerSearch';

const useStyles = makeStyles(registrationOrganizerStyle);

const LiveStreamOrganizer = (props) => {
    const {
        handleObjLiveStream,
        errorText,
        startDate,
        endDate,
        isAdmin,
        customTextLivestream,
        customText,
        isLanguage,
    } = props;
    const intl = useIntl();
    const classes = useStyles();

    const [tempIndex, setTempIndex] = useState('');
    const [tempDate, setTempDate] = useState('');
    const [eventLiveStream, setEventLiveStream] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const [listLiveStream, setListLiveStream] = useState([]);
    const [tableLiveStream, setTableLiveStream] = useState([]);
    const [checkDate, setCheckDate] = useState(false);

    const formatDateCallAPI = (data) => (format(new Date(data), 'yyyy-MM-dd'));

    useEffect(() => {
        setEventLiveStream({
            ...eventLiveStream,
            cost: 0,
            stime: format(new Date().getTime(), 'H:mm'),
            etime: format(new Date().getTime(), 'H:mm'),
            dates: [formatDateCallAPI(changeDateStringToFormat(startDate))],
        });
    }, []);

    useEffect(() => {
        if (!_.isEmpty(tableLiveStream)) {
            const tableData = tableLiveStream.map((item, index) => [
                item.title,
                item.speaker,
                `${item.stime}~${item.etime}`,
                (item.dates).map((date) => <p>{[date.slice(0, 4), '/', date.slice(5, 7), '/', date.slice(8, 10)].join('')}</p>),
                parseFloat(item.cost) === 0 ? intl.formatMessage({ id: 'common.free' }) : `${parseFloat(item.cost)}円`,
                <IconButton
                    onClick={() => removeLiveStream(index)}
                >
                    <Trash />
                </IconButton>,
            ]);
            setListLiveStream(tableData);
            handleObjLiveStream(tableLiveStream);
        }
    }, [tableLiveStream]);

    useEffect(() => {
        if (tempDate && !_.isUndefined(tempIndex) && !_.isEmpty(eventLiveStream.dates)) {
            const tempArrDate = [];
            eventLiveStream.dates.forEach((item, index) => {
                if (index === tempIndex) {
                    tempArrDate.push(tempDate);
                } else {
                    tempArrDate.push(item);
                }
            });
            setEventLiveStream({ ...eventLiveStream, dates: tempArrDate });
            setTempDate('');
            setTempIndex('');
        }
    }, [tempDate, tempIndex]);

    const validate = () => {
        const {
            speaker,
            speaker_en,
            title,
            title_en,
            stime,
            etime,
            cost,
            dates,
        } = eventLiveStream;
        const validateRules = {
            speaker: [
                () => Validation.isNotEmpty(speaker),
                () => Validation.validateMaxLength(speaker, 255),
            ],
            title: [
                () => Validation.isNotEmpty(title),
                () => Validation.validateMaxLength(title, 500),
            ],
            stime: [() => Validation.isNotEmpty(stime),
                () => Validation.validateTimeFormat(stime),
            ],
            etime: [() => Validation.isNotEmpty(etime),
                () => Validation.validateTimeFormat(etime),
            ],
            rangerTime: [() => Validation.validateTimeRanger(stime, etime),
                () => Validation.validateTimeSame(stime, etime),
            ],
            cost: [
                () => Validation.isNotEmpty(cost),
            ],
            dates: [
                () => Validation.validateDuplicateDates(dates),
            ],
        };

        if (isLanguage) {
            validateRules.speaker_en = [
                () => Validation.isNotEmpty(speaker_en),
                () => Validation.validateLatin(speaker_en),
                () => Validation.validateMaxLength(speaker_en, 255),
            ];
            validateRules.title_en = [
                () => Validation.isNotEmpty(title_en),
                () => Validation.validateLatin(title_en),
                () => Validation.validateMaxLength(title_en, 500),
            ];
        }

        const validateResult = Validation.validateAllRules(validateRules);
        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            return false;
        }
        setValidateResults({});
        return true;
    };

    const changeDateStringToFormat = (date) => new Date(parseInt(date.slice(0, 4), 10), parseInt(date.slice(5, 7), 10) - 1, parseInt(date.slice(8, 10), 10));

    const handleSetParams = useCallback((field, valueChange) => {
        setEventLiveStream((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const removeLiveStream = (index) => {
        const tableLiveStreamUpdate = tableLiveStream;
        tableLiveStreamUpdate.splice(index, 1);
        const listLiveStreamUpdate = listLiveStream;
        listLiveStreamUpdate.splice(index, 1);
        setListLiveStream([...listLiveStreamUpdate]);
        setTableLiveStream([...tableLiveStreamUpdate]);
    };

    const handleChangeDate = (sdate, key) => {
        setTempIndex(key);
        setTempDate(formatDateCallAPI(sdate));
    };

    const addDate = () => {
        const date = formatDateCallAPI(changeDateStringToFormat(startDate));
        setEventLiveStream({ ...eventLiveStream, dates: [...eventLiveStream.dates, date] });
    };

    const removeDate = (key) => {
        if (eventLiveStream.dates.length === 1) return;
        eventLiveStream.dates.splice(key, 1);
        const tempArrDate = eventLiveStream.dates;
        setEventLiveStream({ ...eventLiveStream, dates: tempArrDate });
    };

    const addLiveStream = () => {
        const {
            speaker,
            title,
            stime,
            etime,
            cost,
            dates,
        } = eventLiveStream;

        if (validate() && checkNewDate(eventLiveStream.dates)) {
            if (speaker && title && stime && etime && dates) {
                let newLiveStream = {
                    speaker,
                    title,
                    dates,
                    stime,
                    etime,
                    cost,
                };
                if (isLanguage) {
                    newLiveStream = {
                        ...newLiveStream,
                        speaker_en: eventLiveStream.speaker_en,
                        title_en: eventLiveStream.title_en,
                    };
                }
                setTableLiveStream([...tableLiveStream, newLiveStream]);

                const dataDefault = {
                    title: '',
                    speaker: '',
                    stime: format(new Date().getTime(), 'H:mm'),
                    etime: format(new Date().getTime(), 'H:mm'),
                    dates: [formatDateCallAPI(changeDateStringToFormat(startDate))],
                    cost: 0,
                };

                setEventLiveStream(isLanguage ? { ...dataDefault, title_en: '', speaker_en: '' } : dataDefault);
            }
        }
    };

    const checkNewDate = (date) => {
        let listDate = [];
        listDate = listDate.concat(date);
        if (!_.isEmpty(tableLiveStream)) {
            (tableLiveStream || []).forEach((item) => {
                listDate = listDate.concat(item.dates);
            });
        }
        if (Validation.validateDuplicateDates(listDate).result === 'success') {
            setCheckDate(false);
            return true;
        }
        setCheckDate(true);
        return false;
    };

    const disableDates = (date) => {
        let listDateDisable = [];
        if (!_.isEmpty(tableLiveStream)) {
            (tableLiveStream || []).forEach((item) => {
                listDateDisable = listDateDisable.concat(item.dates);
            });
        }
        return listDateDisable.includes(format(date, 'yyyy-MM-dd'));
    };

    return (
        <>
            <GridContainer justify="center" className={classes.spacing}>
                <GridItem xs={12} md={10}>
                    <fieldset className={classes.booth} style={{ minWidth: 'auto' }}>
                        <legend>
                            <CustomTypography variant="h5" color="primary" bold>
                                {customText?.field_name?.livestream || intl.formatMessage({ id: 'userRouters.liveStream' })}
                            </CustomTypography>
                        </legend>
                        <GridContainer>
                            <GridItem xs={12} md={12}>
                                <GridContainer className={classes.padding15}>
                                    <GridItem xs={12} md={3}>
                                        <CustomTypography required>
                                            {isLanguage
                                                ? `${customTextLivestream?.field_name?.title || intl.formatMessage({ id: 'common.title' })} ${intl.formatMessage({ id: 'register.ja' })}`
                                                : customTextLivestream?.field_name?.title || intl.formatMessage({ id: 'common.title' })}
                                        </CustomTypography>
                                    </GridItem>
                                    <GridItem xs={12} md={9}>
                                        <CleanableInput
                                            defaultValue={eventLiveStream.title}
                                            onChange={(valueInput) => handleSetParams('title', valueInput)}
                                            error={!!validateResults.title}
                                            helperText={validateResults.title
                                                ? intl.formatMessage(
                                                    { id: validateResults.title.message },
                                                    { maxLength: 500 },
                                                ) : ''}
                                        />
                                    </GridItem>
                                </GridContainer>
                                {isLanguage && (
                                    <GridContainer className={classes.padding15}>
                                        <GridItem xs={12} md={3}>
                                            <CustomTypography required>
                                                {`${customTextLivestream?.field_name?.title || intl.formatMessage({ id: 'common.title' })} ${intl.formatMessage({ id: 'register.en' })}`}
                                            </CustomTypography>
                                        </GridItem>
                                        <GridItem xs={12} md={9}>
                                            <CleanableInput
                                                defaultValue={eventLiveStream.title_en}
                                                onChange={(valueInput) => handleSetParams('title_en', valueInput)}
                                                error={!!validateResults.title_en}
                                                helperText={validateResults.title_en
                                                    ? intl.formatMessage(
                                                        { id: validateResults.title_en.message },
                                                        { maxLength: 500 },
                                                    ) : ''}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                )}
                                <GridContainer className={classes.padding15}>
                                    <GridItem xs={12} md={3}>
                                        <CustomTypography required>
                                            {isLanguage
                                                ? `${customTextLivestream?.field_name?.performer || intl.formatMessage({ id: 'common.performer' })} ${intl.formatMessage({ id: 'register.ja' })}`
                                                : customTextLivestream?.field_name?.performer || intl.formatMessage({ id: 'common.performer' })}
                                        </CustomTypography>
                                    </GridItem>
                                    <GridItem xs={12} md={9}>
                                        <CleanableInput
                                            defaultValue={eventLiveStream.speaker}
                                            onChange={(valueInput) => handleSetParams('speaker', valueInput)}
                                            error={!!validateResults.speaker}
                                            helperText={validateResults.speaker
                                                ? intl.formatMessage(
                                                    { id: validateResults.speaker.message },
                                                    { maxLength: 255 },
                                                ) : ''}
                                        />
                                    </GridItem>
                                </GridContainer>
                                {isLanguage && (
                                    <GridContainer className={classes.padding15}>
                                        <GridItem xs={12} md={3}>
                                            <CustomTypography required>
                                                {`${customTextLivestream?.field_name?.performer || intl.formatMessage({ id: 'common.performer' })} ${intl.formatMessage({ id: 'register.en' })}`}
                                            </CustomTypography>
                                        </GridItem>
                                        <GridItem xs={12} md={9}>
                                            <CleanableInput
                                                defaultValue={eventLiveStream.speaker_en}
                                                onChange={(valueInput) => handleSetParams('speaker_en', valueInput)}
                                                error={!!validateResults.speaker_en}
                                                helperText={validateResults.speaker_en
                                                    ? intl.formatMessage(
                                                        { id: validateResults.speaker_en.message },
                                                        { maxLength: 255 },
                                                    ) : ''}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                )}
                                <GridContainer className={classes.padding15}>
                                    <GridItem xs={12} md={3}>
                                        <CustomTypography required>
                                            {customTextLivestream?.field_name?.dates || intl.formatMessage({ id: 'common.schedule' })}
                                        </CustomTypography>
                                    </GridItem>
                                    <GridItem xs={12} md={9} style={{ marginLeft: '-15px' }}>
                                        {
                                            (eventLiveStream.dates || []).map((date, key) => (
                                                <GridContainer key={+key} style={{ marginBottom: '20px' }}>
                                                    <GridItem xs={7} md={7}>
                                                        <SingleDatePickerSearch
                                                            onChange={(sdate) => handleChangeDate(sdate, key)}
                                                            defaultValue={date}
                                                            minDate={changeDateStringToFormat(startDate)}
                                                            maxDate={changeDateStringToFormat(endDate)}
                                                            error={!!validateResults.dates}
                                                            shouldDisableDate={disableDates}
                                                        />
                                                    </GridItem>
                                                    <GridItem xs={5} md={5}>
                                                        <IconButton
                                                            size="small"
                                                            classes={{ root: classes.roomRemoveButton }}
                                                            onClick={() => removeDate(key)}
                                                        >
                                                            <IndeterminateCheckBoxIcon />
                                                        </IconButton>
                                                    </GridItem>
                                                </GridContainer>
                                            ))
                                        }
                                        {
                                            validateResults.dates
                                                ? (
                                                    <span className={classes.messageError}>
                                                        { intl.formatMessage({ id: validateResults.dates.message }) }
                                                    </span>
                                                )
                                                : ''
                                        }
                                        {
                                            checkDate && (
                                                <span className={classes.messageError}>
                                                    {intl.formatMessage({ id: 'common.livestreamRegister' }, {
                                                        namePage: customText?.field_name?.livestream || 'userRouters.liveStream',
                                                    })}
                                                </span>
                                            )
                                        }
                                        <GridContainer>
                                            <GridItem>
                                                <IconButton
                                                    size="small"
                                                    classes={{ root: classes.timeAddButton }}
                                                    onClick={addDate}
                                                >
                                                    <AddBoxIcon />
                                                </IconButton>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>

                                <GridContainer className={classes.padding15}>
                                    <GridItem xs={12} md={3}>
                                        <CustomTypography required>
                                            {customTextLivestream?.field_name?.time_zone || intl.formatMessage({ id: 'timeRange' })}
                                        </CustomTypography>
                                    </GridItem>
                                    <GridItem xs={12} md={9} className={isAdmin ? classes.time_ranger_admin : classes.time_ranger}>
                                        <TimeRanger
                                            defaultStartTime={eventLiveStream.stime}
                                            defaultEndTime={eventLiveStream.etime}
                                            onChangeStartTime={(stime) => handleSetParams('stime', stime)}
                                            onChangeEndTime={(etime) => handleSetParams('etime', etime)}
                                            error={{
                                                stime: !!validateResults.stime,
                                                etime: !!validateResults.etime,
                                                rangerTime: !!validateResults.rangerTime,
                                            }}
                                            helperTexts={{
                                                stime: validateResults.stime
                                                    ? intl.formatMessage({ id: validateResults.stime.message }) : '',
                                                etime: validateResults.etime
                                                    ? intl.formatMessage({ id: validateResults.etime.message }) : '',
                                                rangerTime: validateResults.rangerTime
                                                    ? intl.formatMessage({ id: validateResults.rangerTime.message }) : '',
                                            }}
                                        />
                                    </GridItem>
                                </GridContainer>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    justify="flex-end"
                                    alignItems="flex-end"
                                    style={{ marginTop: '15px' }}
                                >
                                    <Button color="info" gradient size="md" onClick={addLiveStream}>
                                        {intl.formatMessage({ id: 'register' })}
                                    </Button>
                                </Box>
                                {listLiveStream.length > 0 && (
                                    <CustomTable
                                        tableHead={[
                                            customTextLivestream?.field_name?.title || intl.formatMessage({ id: 'common.title' }),
                                            customTextLivestream?.field_name?.performer || intl.formatMessage({ id: 'common.performer' }),
                                            customTextLivestream?.field_name?.time_zone || intl.formatMessage({ id: 'timeRange' }),
                                            customTextLivestream?.field_name?.dates || intl.formatMessage({ id: 'common.schedule' }),
                                            customTextLivestream?.field_name?.paid_and_free || intl.formatMessage({ id: 'register.price' }),
                                            customTextLivestream?.field_name?.performer || 'action',
                                        ]}
                                        tableHeadWidth={['37%', '20%', '18%', '15%', '5%', '5%']}
                                        tableData={listLiveStream}
                                        className={classes.maxHeight}
                                    />
                                )}
                            </GridItem>
                            <FormHelperText error className={`MuiFormHelperText-contained ${classes.margin28}`}>
                                {errorText}
                            </FormHelperText>

                        </GridContainer>
                    </fieldset>
                </GridItem>
            </GridContainer>
        </>
    );
};

LiveStreamOrganizer.defaultProps = {
    handleObjLiveStream: undefined,
    errorText: '',
    startDate: '',
    endDate: '',
    isAdmin: false,
    customTextLivestream: {},
    customText: {},
    isLanguage: false,
};

LiveStreamOrganizer.propTypes = {
    handleObjLiveStream: PropTypes.func,
    errorText: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    isAdmin: PropTypes.bool,
    customTextLivestream: PropTypes.oneOfType([() => null, PropTypes.object]),
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    isLanguage: PropTypes.bool,
};

export default LiveStreamOrganizer;
